import { useLayoutEffect } from 'react';

/** Home mounts under the HTML shell: its independent requests must all settle. */
export function useHomeStartup(loading: boolean, error: boolean) {
  useLayoutEffect(() => {
    const root = document.getElementById('root');
    if (!root || !document.getElementById('startup-splash')) return;
    root.dataset.homeStartup = error ? 'error' : loading ? 'pending' : 'ready';
    return () => { delete root.dataset.homeStartup; };
  }, [loading, error]);
}

/** One-shot, route-aware reveal. No minimum duration and no background overlay. */
export function useStartupReveal(authReady: boolean, home: boolean, route: string) {
  useLayoutEffect(() => {
    const splash = document.getElementById('startup-splash');
    const root = document.getElementById('root');
    if (!splash || !root || !authReady) return;
    let cancelled = false;
    let revision = 0;
    const check = async () => {
      const current = ++revision;
      if (splash.dataset.failed || !root.childElementCount) return;
      if (home && root.dataset.homeStartup === 'error') {
        splash.dataset.failed = 'true';
        splash.querySelector<HTMLElement>('.startup-bar')!.hidden = true;
        splash.querySelector('[role="status"]')!.textContent = 'Не удалось загрузить данные. Проверьте соединение и повторите.';
        splash.querySelector<HTMLButtonElement>('#startup-retry')!.hidden = false;
        return;
      }
      if (home && root.dataset.homeStartup !== 'ready') return;
      // Only mounted, initially visible assets, never catalogues or other routes.
      const images = Array.from(root.querySelectorAll('img')).filter(image => {
        const rect = image.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0 && rect.top < innerHeight && rect.bottom > 0;
      });
      await Promise.all([
        document.fonts.ready,
        ...images.map(image => image.decode().catch(() => undefined)),
      ]);
      // Allow font metrics and decoded images to participate in a committed layout.
      await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
      if (cancelled || current !== revision || splash.dataset.failed) return;
      root.inert = false;
      splash.remove();
      observer.disconnect();
    };
    const observer = new MutationObserver(() => { void check(); });
    observer.observe(root, { childList: true, subtree: true, attributes: true });
    void check();
    return () => { cancelled = true; observer.disconnect(); };
  }, [authReady, home, route]);
}
